import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  ForwardChevrons,
  Keyboard,
  Minus,
  Plus,
  Trash2,
  X,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  EntryTypes,
  OverdueContextMenu,
} from 'shared/components/ui/task-detail-checkbox/overdue-context-menu';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { isNumber } from 'shared/utils/is-number';

enum StateOptions {
  Complete = 'complete',
  Skip = 'skip',
  Increment = 'increment',
  Decrement = 'decrement',
  Manual = 'manual',
  Failure = 'failure',
  Clear = 'clear',
}

export type CheckboxContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside'
> & {
  name: string;
  target?: number | null;
  overdueDate?: Timestamp | null;
  onOption: (
    type: ScheduleEntryState,
    options: { value?: number; increment?: number; date: Timestamp },
  ) => void;
  onManual?: (date: Timestamp) => void;
  onClose: () => void;
};

export const CheckboxContextMenu: React.FC<CheckboxContextMenuProps> = ({
  name,
  target,
  overdueDate,
  onOption,
  onManual,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<StateOptions>();
  const today = useToday();

  const { menuOpen: overdueMenuOpen, openMenu: openOverdueMenu } =
    useOpenMenu();

  const stateExecutionMap = {
    [StateOptions.Complete]: (date: Timestamp) =>
      onOption(ScheduleEntryState.Complete, {
        value: target ?? undefined,
        date,
      }),
    [StateOptions.Skip]: (date: Timestamp) =>
      onOption(ScheduleEntryState.Skip, { date }),
    [StateOptions.Increment]: (date: Timestamp) =>
      onOption(ScheduleEntryState.Complete, { increment: 1, date }),
    [StateOptions.Decrement]: (date: Timestamp) =>
      onOption(ScheduleEntryState.Complete, { increment: -1, date }),
    [StateOptions.Manual]: (date: Timestamp) => onManual?.(date),
    [StateOptions.Failure]: (date: Timestamp) =>
      onOption(ScheduleEntryState.Failure, { date }),
    [StateOptions.Clear]: (date: Timestamp) =>
      onOption(ScheduleEntryState.Complete, { value: 0, date }),
  };

  const onDate = (date: Timestamp) => stateExecutionMap[type!]?.(date);

  const itemOnClickGenerator =
    (stateOption: StateOptions) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (overdueDate && stateOption !== StateOptions.Clear) {
        setType(stateOption);
        openOverdueMenu();
        return;
      }

      stateExecutionMap[stateOption]?.(today);
      onClose();
    };

  const onComplete = itemOnClickGenerator(StateOptions.Complete);

  const onIncrement = itemOnClickGenerator(StateOptions.Increment);

  const onDecrement = itemOnClickGenerator(StateOptions.Decrement);

  const _onManual = itemOnClickGenerator(StateOptions.Manual);

  const onSkip = itemOnClickGenerator(StateOptions.Skip);

  const onFail = itemOnClickGenerator(StateOptions.Failure);

  const onClear = itemOnClickGenerator(StateOptions.Clear);

  const entryType =
    type === StateOptions.Skip
      ? EntryTypes.Skip
      : type === StateOptions.Failure
        ? EntryTypes.Failure
        : EntryTypes.Complete;

  return overdueMenuOpen && overdueDate ? (
    <OverdueContextMenu
      {...rest}
      name={name}
      overdueDate={overdueDate}
      onDate={onDate}
      onClose={onClose}
      type={entryType}
    />
  ) : (
    <PopupMenu position="bottom-start" onOutside={onClose} {...rest}>
      <PopupMenuList hasBottomBorder>
        {isNumber(target) && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onComplete}
              start={<Icon icon={CheckCircle2} />}
            >
              {t('context-menus.calendar.day.complete.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        <PopupMenuListItem>
          <PopupMenuButton onClick={onIncrement} start={<Icon icon={Plus} />}>
            +1
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton onClick={onDecrement} start={<Icon icon={Minus} />}>
            -1
          </PopupMenuButton>
        </PopupMenuListItem>
        {!!onManual && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={_onManual}
              start={<Icon icon={Keyboard} />}
            >
              {t('context-menus.calendar.day.log-progress.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
      </PopupMenuList>

      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onSkip}
            start={<Icon icon={ForwardChevrons} />}
          >
            {t('context-menus.calendar.day.skip.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton onClick={onFail} start={<Icon icon={X} />}>
            {t('context-menus.calendar.day.fail.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton onClick={onClear} start={<Icon icon={Trash2} />}>
            {t('context-menus.calendar.day.clear.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
